import {
  GET_BILL_HISTORY_SUCCESS,
  GET_BILL_HISTORY_FAIL,
  SET_GET_BILL_HISTORY_LOADING,
  GET_SINGLE_BILL_DATA_SUCCESS,
  GET_SINGLE_BILL_DATA_FAIL,
  SET_GET_SINGLE_BILL_DATA_LOADING,
} from './types';
import store from '../../../../store';
import Axios from '../../../../common/util/Axios';

const appendEntityFilters = (queryParams, filters = {}) => {
  if (filters.cashierRegistryId) {
    queryParams.cashierRegistryId = filters.cashierRegistryId;
  }
  if (filters.cashierId) {
    queryParams.cashierId = filters.cashierId;
  }
  if (filters.customerId) {
    queryParams.customerId = filters.customerId;
  }
  if (filters.orderType) {
    queryParams.orderType = filters.orderType;
  }
  if (filters.businessVertical) {
    queryParams.businessVertical = filters.businessVertical;
  }
  if (filters.restaurantMode === true) {
    queryParams.restaurantMode = 'true';
  }
  if (filters.periodType === 'businessDay') {
    queryParams.periodType = 'businessDay';
    queryParams.startBusinessDayId = filters.startBusinessDayId;
    queryParams.endBusinessDayId = filters.endBusinessDayId;
  }
};

/**
 * Get bill history with filters and pagination
 */
export const getBillHistory = (filters = {}) => async (dispatch) => {
  try {
    dispatch({
      type: SET_GET_BILL_HISTORY_LOADING,
      payload: true,
    });

    const companyId = store.getState().authState.companyId;
    const branchId = store.getState().authState.branchId;

    const queryParams = {
      bill_no: filters.bill_no || '',
      start_date: filters.start_date || '',
      end_date: filters.end_date || '',
      page: filters.page || 1,
      limit: filters.limit || 10,
      companyId: companyId || '',
      branchId: branchId || '',
    };
    appendEntityFilters(queryParams, filters);

    const res = await Axios.get(
      '/api/dashboard/bill-history/get-filtered-bills',
      {
        params: queryParams,
      }
    );

    if (res.data && res.data.success) {
      dispatch({
        type: GET_BILL_HISTORY_SUCCESS,
        payload: {
          data: res.data.data,
          pagination: res.data.pagination,
        },
      });
    } else {
      dispatch({
        type: GET_BILL_HISTORY_FAIL,
        payload: res.data?.error || 'Failed to fetch bill history',
      });
    }
  } catch (error) {
    console.error('Error fetching bill history:', error);
    dispatch({
      type: GET_BILL_HISTORY_FAIL,
      payload: error.response?.data?.error || error.message || 'Error fetching bill history',
    });
  } finally {
    dispatch({
      type: SET_GET_BILL_HISTORY_LOADING,
      payload: false,
    });
  }
};

/**
 * Export bill history as Excel (PDF support to be added in future)
 */
export const exportBillHistoryExcel = (
  params = {},
  format = 'excel',
  reportType 
) => async () => {
  try {
    const companyId = store.getState().authState.companyId;
    const branchId = store.getState().authState.branchId;

    const getTimestampString = () => {
      const pad = (num) => String(num).padStart(2, '0');
      const d = new Date();
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
        d.getDate()
      )}_${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;
    };

    const timestamp = getTimestampString();
    const isPdf = format === 'pdf';

    const isPurchaseItemReport = reportType === 'purchase_item_report';

    const billHistoryColumns = [
      { key: 'billNo', header: 'Bill No' },
      { key: 'cashier_name', header: 'Cashier' },
      { key: 'customer_name', header: 'Customer' },
      { key: 'paymentMethod', header: 'Payment Method' },
      { key: 'totalAmount', header: 'Total Amount' },
      { key: 'totalDiscount', header: 'Discount' },
      { key: 'payableAmount', header: 'Payable Amount' },
      { key: 'paidAmount', header: 'Paid Amount' },
      { key: 'return_amount', header: 'Return Amount' },
      { key: 'status', header: 'Status' },
      { key: 'cost', header: 'Cost' },
      { key: 'profit', header: 'Profit' },
      { key: 'createdDate', header: 'Created Date' },
    ];

    const purchaseItemColumns = [     
      { key: 'billNo', header: 'Bill Number' },
      { key: 'customerName', header: 'Customer Name' },
      { key: 'customerPhone', header: 'Customer Phone' },
      { key: 'itemCode', header: 'Item Code' },
      { key: 'itemName', header: 'Item Name' },
      { key: 'unit_price', header: 'Unit Price' },
      { key: 'discount', header: 'Discount' },
      { key: 'quantity', header: 'Quantity' },
      { key: 'total_amount', header: 'Total Amount' },
      { key: 'cost', header: 'Cost' },
      { key: 'returnQuantity', header: 'Return Quantity' },
      { key: 'profit', header: 'Profit' },
      { key: 'createdDate', header: 'Created Date' },
    ];

    const reportPrefix = isPurchaseItemReport ? 'purchase_item_report' : 'bill_history_export';
    const requestBody = {
      columns: isPurchaseItemReport ? purchaseItemColumns : billHistoryColumns,
      filename: isPdf ? `${reportPrefix}_${timestamp}.pdf` : `${reportPrefix}_${timestamp}.xlsx`,
      sheetName: isPurchaseItemReport ? 'Purchase Item Report' : 'Bill History',
      format: isPdf ? 'pdf' : 'excel',
      reportType,
      companyId,
      branchId,
      ...params,
    };
    const acceptHeader = isPdf
      ? 'application/pdf'
      : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

    const response = await Axios.post(
      '/api/dashboard/bill-history/export-excel',
      requestBody,
      {
        responseType: 'arraybuffer',
        headers: { Accept: acceptHeader },
      }
    );

    const contentType = response?.headers?.['content-type'] || '';
    const isJson = contentType.toLowerCase().includes('application/json');
    if (isJson && response.data) {
      const text = new TextDecoder().decode(response.data);
      const errBody = (() => { try { return JSON.parse(text); } catch (_) { return {}; } })();
      throw new Error(errBody.message || errBody.error || text || 'Export failed');
    }

    const contentDisposition = response?.headers?.['content-disposition'];
    let downloadFileName =
     requestBody.filename || (isPdf ? 'bill_history_export.pdf' : 'bill_history_export.xlsx');
    if (contentDisposition && contentDisposition.includes('filename=')) {
      const match = contentDisposition.match(
        /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i
      );
      downloadFileName = decodeURIComponent(
        match?.[1] || match?.[2] || downloadFileName
      );
    }

    const blobType = isPdf
      ? 'application/pdf'
      : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const blob = new Blob([response.data], { type: blobType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', downloadFileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Export bill history failed', error);
  }
};

/**
 * Get bill details by ID
 */
export const getBillDetailsById = (billId) => async (dispatch) => {
  try {
    dispatch({
      type: SET_GET_SINGLE_BILL_DATA_LOADING,
      payload: true,
    });

    const companyId = store.getState().authState.companyId;
    const branchId = store.getState().authState.branchId;

    const res = await Axios.get(
      '/api/dashboard/bill-history/get-bill-details-by-id',
      {
        params: {
          billId: billId,
          companyId: companyId || '',
          branchId: branchId || '',
        },
      }
    );

    if (res.data && res.data.success) {
      dispatch({
        type: GET_SINGLE_BILL_DATA_SUCCESS,
        payload: res.data.data,
      });
      return true;
    } else {
      dispatch({
        type: GET_SINGLE_BILL_DATA_FAIL,
        payload: res.data?.error || 'Failed to fetch bill details',
      });
      return false;
    }
  } catch (error) {
    console.error('Error fetching bill details:', error);
    dispatch({
      type: GET_SINGLE_BILL_DATA_FAIL,
      payload: error.response?.data?.error || error.message || 'Error fetching bill details',
    });
    return false;
  } finally {
    dispatch({
      type: SET_GET_SINGLE_BILL_DATA_LOADING,
      payload: false,
    });
  }
};
